import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import {
  CompiereDataGridGroupModel,
  CompiereDataGridResponseJSON,
  DataStore,
  DataStoreRequest,
} from '@compiere-ws/models/compiere-data-json';
import { ParentChildRelation, TreeCompiereJSON } from '@compiere-ws/models/compiere-tree-json';
import { IAutocomplete } from '@iupics-components/models/autocomplete-interfaces';
import InputSwitchUiComponent from '@iupics-components/standard/fields/input-switch-ui/input-switch-ui.component';
import InputTextUiComponent from '@iupics-components/standard/fields/input-text-ui/input-text-ui.component';
import GridTabInfinityScrollUiComponent from '@iupics-components/standard/grid/grid-tab-infinity-scroll-ui/grid-tab-infinity-scroll-ui.component';
import ModalUiComponent from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import { IupicsTableDataHeader } from '@iupics-manager/models/iupics-data';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { DynamicContainerDirective } from '@iupics-util/directives/dynamic-container.directive';
import { TranslateModule } from '@ngx-translate/core';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { AngularSplitModule } from 'angular-split';
import { cloneDeep } from 'lodash';
import { TreeDragDropService, TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SidebarModule } from 'primeng/sidebar';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule, TreeNodeDropEvent } from 'primeng/tree';
import { Observable, Subscription, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import SpecificWindowUiComponent, {SpecificGridQuery} from '../specific-window-ui/specific-window-ui.component';
import { TreeMaintenanceService } from './tree-maintenance.service';
import {
  buildValidationTreeNode,
  findInTree,
  formatTree,
  getColumnsRequest,
  getDataFromTable,
  getIconForItem,
  getInTree,
  getNbLevel,
  isInTree,
  moveToNewIndex,
  pushInStack,
  uniformData,
} from './tree-maintenance.util';

@Component({
  selector: 'iu-tree-maintenance-ui',
  templateUrl: './tree-maintenance-ui.component.html',
  styleUrls: ['./tree-maintenance-ui.component.scss'],
  standalone: true,
  providers: [TreeDragDropService, TreeMaintenanceService],
  imports: [
    ButtonModule,
    TooltipModule,
    DynamicContainerDirective,
    AngularSplitModule,
    InputTextUiComponent,
    TreeModule,
    GridTabInfinityScrollUiComponent,
    SidebarModule,
    InputSwitchUiComponent,
    ModalUiComponent,
    NgClass,
    TranslateModule,
  ],
})
export default class TreeMaintenanceUIComponent
  extends SpecificWindowUiComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  /* View Children */
  @ViewChild('dynContainer') dynContainer: ElementRef<HTMLDivElement>;
  @ViewChild('leftBtnContainer') leftBtnContainer: ElementRef<HTMLDivElement>;
  @ViewChild('searchTreeInputComponent', { static: false }) searchTreeInputComponent: InputTextUiComponent;
  @ViewChild('gridTab', { static: false }) gridTab: GridTabInfinityScrollUiComponent;
  @ViewChild('searchTreeDiv', { static: false }) searchTreeDiv: ElementRef<HTMLDivElement>;
  @ViewChild('searchGridDiv', { static: false }) searchGridDiv: ElementRef<HTMLDivElement>;

  service = inject(TreeMaintenanceService);

  /* Tree stuff */
  treeID: IAutocomplete;
  treeType: string;
  treeData: TreeCompiereJSON;
  treeNodes: TreeNode[];
  private _selectedTreeNodes: TreeNode[] = [];

  get selectedTreeNodes(): TreeNode[] {
    return this._selectedTreeNodes;
  }

  set selectedTreeNodes(selection: TreeNode[]) {
    this._selectedTreeNodes = selection
      .sort((a, b) => a.parent.children.indexOf(a) - b.parent?.children.indexOf(b))
      .sort((a, b) => a.parent?.data.id - b.parent?.data.id);
    this.isMovementDisabled =
      this._selectedTreeNodes
        .map((n) => getNbLevel(n))
        .reduce((unique, item) => (unique.includes(item) ? unique : [...unique, item]), [] as number[]).length > 1;
  }

  isTreeLoading = false;
  treeSearchValue: string;
  treeNodeCount = 0;
  /* true once the tree was changed locally and not saved yet */
  isDirty = false;
  private labelPartsCache = new Map<string, TreeLabelPart[]>();
  private labelPartsSearch: string;

  /* Table stuff */
  private tableID: number;
  private tableName: string;
  colTableHeaders: IupicsTableDataHeader[] = [];
  searchColumns: any[] = [];
  gridSearchValue: string;
  selectedLines: any[] = [];
  gridRowCount = -1;
  rowClassRules = {
    'tree-maintenance-row-is-summary': (params: any) => (params.data ? params.data.IsSummary === 'Y' : false),
  };

  /* Configuration stuff */
  isLetterByLetterSearch = true;
  isDeleteAll = false;
  isAddAll = false;
  isMovementDisabled = false;

  /* Private stuff */
  private removed_ids: number[] = [];
  private added_ids: number[] = [];
  private columnsToDisplay: { [tableName: string]: string[] } = {
    AD_Menu: ['$COL_ID', 'Name', 'Description', 'IsSummary', 'Action'],
    M_Product: ['$COL_ID', 'Name', 'Description', 'IsSummary', 'AD_PrintColor_ID'],
    C_BPartner: ['$COL_ID', 'Name', 'Description', 'IsSummary', 'AD_PrintColor_ID'],
    AD_Org: ['$COL_ID', 'Name', 'Description', 'IsSummary', 'AD_PrintColor_ID'],
    C_Campaign: ['$COL_ID', 'Name', 'Description', 'IsSummary', 'AD_PrintColor_ID'],
    _other_: ['$COL_ID', 'Name', 'Description', 'IsSummary'],
  };
  private mainSub: Subscription;
  private gridSub: Subscription;
  private searchInTreeListener: () => void = () => {};

  /* CSS stuff */
  get splitHeight(): string {
    return this.dynContainer ? `calc(100% - ${this.dynContainer?.nativeElement.clientHeight}px)` : undefined;
  }

  get splitTreeWidth(): string {
    return this.leftBtnContainer ? `calc(100% - ${this.leftBtnContainer?.nativeElement.scrollWidth}px)` : undefined;
  }

  get treeHeight(): string {
    // ? le + 5 c'est le margin-bottom
    return this.searchTreeDiv ? `calc(100% - ${this.searchTreeDiv?.nativeElement.scrollHeight + 5}px)` : '100%';
  }

  get gridHeight(): string {
    // ? le + 5 c'est le margin-bottom
    return this.searchGridDiv ? `calc(100% - ${this.searchGridDiv?.nativeElement.scrollHeight + 5}px)` : '100%';
  }

  private renderer = inject(Renderer2);

  IupicsMenuType = IupicsMenuType;
  tableFormDetailID: number | undefined = undefined;

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    const fieldAD_Tree_ID = this.dataContainers.find((item) => item.data.columnName === 'AD_Tree_ID');
    if (fieldAD_Tree_ID) {
      fieldAD_Tree_ID.fieldValueModified.subscribe((ds: DataStore) => {
        this.handleAD_Tree_ID(ds);
      });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.mainSub?.unsubscribe();
  }

  //#region data management
  private handleAD_Tree_ID(ds: DataStore): void {
    if (ds.data.AD_Tree_ID?.id) {
      setTimeout(() => {
        this.treeID = ds.data.AD_Tree_ID;
        this.isTreeLoading = true;
        this.selectedTreeNodes = [];
        this.selectedLines = [];
        this.treeSearchValue = undefined;
        this.gridSearchValue = undefined;
        this.isDirty = false;
        this.mainSub?.unsubscribe();
        this.mainSub = zip(this.getTree(), this.initDataGrid()).subscribe();
      }, 250);
    }
  }

  private getTree(): Observable<any> {
    return this.store.getDataTree(this.treeID.id, true).pipe(
      tap((_) => {
        this.searchInTreeListener();
        this.searchInTreeListener = this.renderer.listen(
          this.searchTreeInputComponent.inputRef?.nativeElement,
          'keyup',
          ($e: Event) => {
            $e.stopPropagation();
            if (this.isLetterByLetterSearch) {
              this.treeSearchValue = this.searchTreeInputComponent.fieldValue;
              this.treeNodes = this.searchInTree(
                this.treeSearchValue,
                cloneDeep(this.treeData.treeNodes.filter((n) => n.data.id !== -2))
              );
            }
          }
        );
      }),
      map((tree) => {
        tree.treeNodes[0] = formatTree(tree.treeNodes[0]);
        return tree;
      }),
      tap({
        next: (tree) => {
          if (tree) {
            this.treeData = tree;
            this.treeData.treeNodes.push({
              children: [],
              label: 'deleted nodes',
              expanded: false,
              data: {
                color: null,
                description: '',
                id: -2,
                imageIndicator: '',
                isSummary: 'Y',
                name: 'deleted nodes',
              },
              selectable: false,
              icon: null,
            });
            this.treeNodes = cloneDeep(this.treeData.treeNodes.filter((n) => n.data.id !== -2));
          }
          this.updateTreeNodeCount();
          this.isTreeLoading = false;
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 422) {
            this.messageManager.newMessage(
              new IupicsMessage(
                this.translateService.instant('generic.error'),
                this.translateService.instant('tree-maintenance.no-tree-found', {
                  treeName: this.treeID.displayValue,
                  treeID: this.treeID.id,
                }),
                'error',
                err.error
              )
            );
          }
          this.treeData = undefined;
          this.updateTreeNodeCount();
          this.isTreeLoading = false;
        },
      })
    );
  }

  private initDataGrid() {
    return this.service.getTreeMaintenanceTableDefinition(this.treeID.id).pipe(
      tap((response) => {
        this.tableID = response.tableId;
        this.tableName = response.tableName;
        this.treeType = response.treeType;
      }),
      switchMap((_) => {
        const tableName = Object.keys(this.columnsToDisplay).includes(this.tableName) ? this.tableName : '_other_';
        return this.store.getTableData(
          getColumnsRequest(
            this.tableID,
            this.columnsToDisplay[tableName],
            this.connectorService.getIupicsDefaultLanguage().iso_code
          )
        );
      }),
      tap((_response) => {
        this.colTableHeaders = this.buildColumnHeaders(_response);
        this.searchColumns = this.uiCreator.getColumnInfosSync(this.gridViews[0].tabId, true).map((f) => f.fieldEntity);
        this.tableFormDetailID = this.gridViews[0]?.tabId;
        setTimeout(() => {
          this.getDataGrid = this.getDataGrid.bind(this);
          if (this.gridTab?.dataSource) {
            this.gridTab.dataSourceCreated = false;
            this.gridTab?.refresh(true, { specificGridOptions: { isFromInputLaunch: true } });
          }
          this.gridTab?.setView();
        }, 5);
      })
    );
  }

  refresh(event?: Event): any {
    event?.stopPropagation();

    this.removed_ids = [];
    this.added_ids = [];
    this.isDirty = false;

    this.treeData = undefined;
    this.treeNodes = [];
    this.treeNodeCount = 0;
    this.isTreeLoading = true;

    this.gridTab.grid.api.deselectAll();

    this.mainSub?.unsubscribe();
    this.mainSub = zip(this.getTree()).subscribe();
    this.gridTab.dataSourceCreated = false;
    this.gridTab.refresh(true, { specificGridOptions: { isFromInputLaunch: true } });
  }

  saveTree(event: Event): void {
    event.stopPropagation();
    const dataToSave: ParentChildRelation[] = [];
    this.prepareDataToSave(this.treeData.treeNodes, dataToSave, 0);

    const tree = cloneDeep(this.treeData);
    tree.treeNodes = [];
    tree.parentChildRelations = dataToSave.filter(
      (i) =>
        i.node_ID >= 0 &&
        tree.parentChildRelations.findIndex(
          (pcr) => pcr.node_ID === i.node_ID && pcr.parent_ID === i.parent_ID && pcr.seqNo === i.seqNo
        ) < 0
    );

    if (tree.parentChildRelations.length) {
      this.store.saveDataTree(tree).subscribe((_) => {
        this.refresh();
      });
    } else {
      this.refresh();
    }
  }

  private prepareDataToSave(nodes: TreeNode[], dataToSave: ParentChildRelation[], parent_ID: number): void {
    let seqNo = 0;
    for (const node of nodes) {
      seqNo += 10;
      dataToSave.push({ node_ID: node.data.id, parent_ID, seqNo });

      if (node.children && node.children.length > 0) {
        this.prepareDataToSave(node.children, dataToSave, node.data.id);
      }
    }
  }
  //#endregion

  //#region grid management
  getDataGrid: (
    request: DataStoreRequest,
    forceCallWs?: boolean,
    type?: string
  ) => Observable<CompiereDataGridResponseJSON> = (dataStoreRequest: DataStoreRequest, forceCallWs?: boolean) => {
    const tableName = Object.keys(this.columnsToDisplay).includes(this.tableName) ? this.tableName : '_other_';
    Object.assign(
      dataStoreRequest.compiereRequest,
      getDataFromTable(
        this.tableName,
        this.colTableHeaders.reduce<CompiereDataGridGroupModel[]>((acc, columnHeader) => {
          if (this.columnsToDisplay[tableName].includes(columnHeader.field)) {
            acc.push({
              id: columnHeader.field,
              field: columnHeader.field,
              displayName: columnHeader.headerName,
            });
          }
          return acc;
        }, []),
        buildValidationTreeNode(this.tableName, this.treeType, this.treeID, this.removed_ids, this.added_ids),
        this.connectorService.getIupicsDefaultLanguage().iso_code
      ).compiereRequest
    );
    return this.store.getTableData(dataStoreRequest, forceCallWs).pipe(
      tap((response) => {
        // display only: total of available rows, -1 when unknown
        if (!dataStoreRequest.compiereRequest.startRow) {
          this.gridRowCount = response?.lastRow ?? -1;
        }
      })
    );
  };

  private buildColumnHeaders(response: CompiereDataGridResponseJSON): IupicsTableDataHeader[] {
    const columnHeaders: IupicsTableDataHeader[] = [];

    const tableName = Object.keys(this.columnsToDisplay).includes(this.tableName) ? this.tableName : '_other_';

    const datas: object[] = response.data;

    for (const columnName of this.columnsToDisplay[tableName]) {
      const filter =
        columnName === '$COL_ID'
          ? (r: object): boolean => r['IsKey'] === 'Y'
          : (r: object): boolean => r['ColumnName'] === columnName;
      const row = datas.find(filter);
      if (row) {
        const columnHeader: IupicsTableDataHeader = {
          field: row['ColumnName'],
          headerName: row['Name'],
          enableRowGroup: false,
          editable: false,
          hide: false,
          width: 'min-content',
        };
        if (columnName === 'IsSummary') {
          // same Y/N rendering as the standard grids
          columnHeader.cellRenderer = (params) =>
            params.value === 'Y' || params.value === 'N'
              ? `<label class="container-background"><span class="checkbox ${
                  params.value === 'Y' ? 'icon-checkbox-checked' : 'icon-checkbox-crossed'
                }"></span></label>`
              : '';
        }
        columnHeaders.push(columnHeader);
      }
    }

    return columnHeaders;
  }

  searchInGrid(value: string): void {
    this.gridSearchValue = value;
    this.gridTab.grid.api.deselectAll();
      const query: SpecificGridQuery = {
          filters: {
              Name: this.gridSearchValue,
          },
      };
    this.gridTab.refresh(true, query);
  }

  onSelectOnGrid({ data }: { data: any[] }): void {
    this.selectedLines = data;
  }
  //#endregion

  //#region tree management
  private searchInTree(value: string, treeNodes: TreeNode[]): TreeNode<any>[] {
    for (let i = 0; i < treeNodes.length; i++) {
      const treeNode = treeNodes[i];
      if (!treeNode.label || !treeNode.label.toLowerCase().trim().includes(value.toLowerCase().trim())) {
        if (treeNode.data.isSummary) {
          treeNode.children = this.searchInTree(value, treeNode.children);
          if (!treeNode.children.length) {
            treeNodes[i] = undefined;
          } else {
            // show the matches (view copy only)
            treeNode.expanded = true;
          }
        } else {
          treeNodes[i] = undefined;
        }
      }
    }
    return treeNodes.filter((i) => i);
  }

  searchFromFieldValue(value: string): void {
    this.treeSearchValue = value;
    if (value) {
      this.treeNodes = this.searchInTree(value, cloneDeep(this.treeData.treeNodes.filter((n) => n.data.id !== -2)));
    } else {
      this.treeNodes = cloneDeep(this.treeData.treeNodes.filter((n) => n.data.id !== -2));
    }
    this.updateTreeNodeCount();
  }

  private updateTreeNodeCount(): void {
    const count = (nodes: TreeNode[]): number =>
      (nodes ?? []).reduce(
        (acc, n) => (n.data?.id === -2 ? acc : acc + (n.data?.id > -1 ? 1 : 0) + count(n.children)),
        0
      );
    this.treeNodeCount = count(this.treeData?.treeNodes);
  }

  /** Splits a node label around the current tree search, for highlighting */
  getLabelParts(label: string): TreeLabelPart[] {
    const search = (this.treeSearchValue ?? '').toLowerCase().trim();
    if (search !== this.labelPartsSearch) {
      this.labelPartsSearch = search;
      this.labelPartsCache.clear();
    }
    const text = label ?? '';
    let parts = this.labelPartsCache.get(text);
    if (!parts) {
      parts = [];
      const lower = text.toLowerCase();
      let from = 0;
      let idx = search ? lower.indexOf(search) : -1;
      while (idx > -1) {
        if (idx > from) {
          parts.push({ text: text.substring(from, idx), match: false });
        }
        parts.push({ text: text.substring(idx, idx + search.length), match: true });
        from = idx + search.length;
        idx = lower.indexOf(search, from);
      }
      if (from < text.length || !parts.length) {
        parts.push({ text: text.substring(from), match: false });
      }
      this.labelPartsCache.set(text, parts);
    }
    return parts;
  }

  setAllExpanded(event: Event, expanded: boolean): void {
    event?.stopPropagation();
    // top level stays open so collapse-all still shows the first level
    const apply = (nodes: TreeNode[], depth: number) => {
      for (const n of nodes ?? []) {
        if (n.data?.id === -2) {
          continue;
        }
        if (n.children?.length) {
          n.expanded = expanded || depth === 0;
          apply(n.children, depth + 1);
        }
      }
    };
    if (this.treeData?.treeNodes) {
      apply(this.treeData.treeNodes, 0);
    }
    apply(this.treeNodes, 0);
  }

  addInTree(event: Event): void {
    event.stopPropagation();
    if (this.selectedLines?.length) {
      let selectedTreeNode = this.treeData.treeNodes.find((n) => n.data.id === -1);
      if (this.selectedTreeNodes?.length === 1 && this.selectedTreeNodes[0].data.IsSummary === 'Y') {
        selectedTreeNode = this.selectedTreeNodes[0];
      }
      selectedTreeNode.children = selectedTreeNode.children || [];
      const alreadyInTree: string[] = [];
      for (const selectedLine of this.selectedLines) {
        const line = uniformData(selectedLine);
        if (
          isInTree(
            this.treeData.treeNodes.filter((n) => n.data.id === -2),
            line
          )
        ) {
          this.moveInTree({
            originalEvent: document.createEvent('Event'),
            dropNode: selectedTreeNode,
            dragNode: this.treeData.treeNodes
              .filter((n) => n.data.id === -2)[0]
              .children.find((n) => n.data.id === line.id),
          });
          if (this.removed_ids.includes(line.id)) {
            this.removed_ids = this.removed_ids.filter((id) => id !== line.id);
          } else if (!this.added_ids.includes(line.id)) {
            this.added_ids.push(line.id);
          }
          this.isDirty = true;
        } else if (!isInTree(this.treeData.treeNodes, line)) {
          selectedTreeNode.children.push({
            label: line.name,
            data: line,
            expanded: false,
            expandedIcon: line.isSummary ? 'fas fa-folder-open' : '',
            collapsedIcon: line.isSummary ? 'fas fa-folder' : '',
            selectable: true,
            children: line.isSummary ? [] : null,
            icon: !line.isSummary ? getIconForItem(line) : '',
          } as TreeNode);
          if (this.removed_ids.includes(line.id)) {
            this.removed_ids = this.removed_ids.filter((id) => id !== line.id);
          } else if (!this.added_ids.includes(line.id)) {
            this.added_ids.push(line.id);
          }
          this.isDirty = true;
        } else {
          alreadyInTree.push(line.name);
        }
      }
      if (alreadyInTree?.length) {
        this.messageManager.newMessage(
          new IupicsMessage(
            this.translateService.instant('generic.info'),
            this.translateService.instant('tree-maintenance.already-in-tree', {
              nodes: alreadyInTree.join(', '),
            }),
            'message'
          )
        );
      }
      this.searchFromFieldValue(this.treeSearchValue);
    }
  }

  dropNode({ originalEvent, dragNode, dropNode }: TreeNodeDropEvent): void {
    originalEvent?.stopPropagation();
    const oldParent = findInTree(this.treeData.treeNodes, dragNode)?.parent;
    if (oldParent) {
      const oldNode = oldParent.children.splice(
        oldParent.children.findIndex((n) => n.data.id === dragNode.data.id),
        1
      )[0];
      let newParent: TreeNode;
      if (
        originalEvent?.target &&
        (originalEvent.target as HTMLElement).tagName.toLowerCase() === 'li' &&
        (originalEvent.target as HTMLElement).classList.contains('p-treenode-droppoint')
      ) {
        // si on met sur la barre bleue
        newParent = findInTree(this.treeData.treeNodes, dropNode.parent);
        if (!newParent) {
          return;
        }
        const dropIndex = newParent.children.findIndex((n) => n.data.id === dropNode.data.id);
        newParent.children.splice(dropIndex, 0, oldNode);
      } else if (originalEvent?.target && (originalEvent.target as HTMLElement).tagName === 'DIV') {
        // on drop sur un element
        if (!dropNode.data.isSummary) {
          return;
        }
        newParent = findInTree(this.treeData.treeNodes, dropNode);
        if (!newParent) {
          return;
        }
        newParent.children.push(oldNode);
      }
      oldNode.parent = newParent;
      this.isDirty = true;
    }
  }

  trackByFn(index: number, item: any): any {
    return item.data.id;
  }

  private moveInTree(
    { originalEvent, dragNode, dropNode }: { originalEvent: Event; dragNode: TreeNode; dropNode: TreeNode },
    position: 'top' | 'bottom' | 'up' | 'down' | number = 'bottom'
  ): void {
    originalEvent?.stopPropagation();
    let dragStack = [];
    let dropStack = [];
    if (dragNode?.parent) {
      dragStack = pushInStack(dragNode.parent);
    }
    dropStack = pushInStack(dropNode);
    if (dragStack.length) {
      const _dragNodeParent = getInTree(this.treeData.treeNodes, dragStack);
      _dragNodeParent.children.splice(
        _dragNodeParent.children.findIndex((n) => n.data.id === dragNode.data.id),
        1
      );
    }
    if (dropStack.length) {
      const _dropNode = getInTree(this.treeData.treeNodes, dropStack);
      switch (position) {
        case 'bottom': {
          _dropNode.children.push(dragNode);
          break;
        }
        case 'top': {
          _dropNode.children = [dragNode, ..._dropNode.children];
          break;
        }
        case 'down': {
          const downIndex = _dropNode.children.findIndex((n) => n.data.id === dragNode.data.id);
          _dropNode.children = moveToNewIndex(_dropNode.children, downIndex, downIndex + 1);
          break;
        }
        case 'up': {
          const upIndex = _dropNode.children.findIndex((n) => n.data.id === dragNode.data.id);
          _dropNode.children = moveToNewIndex(_dropNode.children, upIndex, upIndex - 1);
          break;
        }
        default: {
          _dropNode.children.push(dragNode);
          const defIndex = _dropNode.children.length - 1;
          _dropNode.children = moveToNewIndex(_dropNode.children, defIndex, position);
        }
      }
      dragNode.parent = dropNode;
    }
  }

  deleteFromTree(event: Event): void {
    event.stopPropagation();
    if (this.selectedTreeNodes?.length) {
      this.isDirty = true;
      this._deleteFromTree(this.selectedTreeNodes);
      this.searchFromFieldValue(this.treeSearchValue);
    }
  }

  private _deleteFromTree(nodes: TreeNode[]): void {
    const fakeDropNode = this.treeData.treeNodes.find((n) => n.data.id === -2);
    for (const fakeDragNode of nodes) {
      if (fakeDragNode.children?.length) {
        this._deleteFromTree(fakeDragNode.children);
        fakeDragNode.children = [];
      }
      this.moveInTree({ originalEvent: document.createEvent('Event'), dragNode: fakeDragNode, dropNode: fakeDropNode });
      fakeDragNode.parent = fakeDropNode;
      if (this.added_ids.includes(fakeDragNode.data.id)) {
        this.added_ids = this.added_ids.filter((id) => id !== fakeDragNode.data.id);
      } else if (!this.removed_ids.includes(fakeDragNode.data.id)) {
        this.removed_ids.push(fakeDragNode.data.id);
      }
    }
  }

  stepUp(event: Event, marker?: string): void {
    event?.stopPropagation();
    if (!this.selectedTreeNodes?.length) {
      return;
    }
    this.isDirty = true;
    if (marker === '*') {
      for (let i = this.selectedTreeNodes.length - 1; i >= 0; i--) {
        const node = this.selectedTreeNodes[i];
        this.moveInTree({ originalEvent: event, dragNode: node, dropNode: this.treeNodes[0] }, 'top');
      }
      this.searchFromFieldValue(this.treeSearchValue);
      this.selectedTreeNodes = this.reselect(this.treeNodes[0]);
    } else {
      for (const node of this.selectedTreeNodes) {
        const { parent } = node;
        const oldIndex = parent.children.indexOf(node);
        if (oldIndex > 0 && !this.selectedTreeNodes.includes(parent.children[oldIndex - 1])) {
          node.parent = undefined;
          // déplacement dans l'arbre visuel
          parent.children = moveToNewIndex(parent.children, oldIndex, oldIndex - 1);
          // déplacement dans le vrai arbre
          this.moveInTree({ originalEvent: event, dragNode: node, dropNode: parent }, 'up');
          node.parent = parent;
        }
      }
    }
  }

  stepDown(event: Event, marker?: string): void {
    event?.stopPropagation();
    if (!this.selectedTreeNodes?.length) {
      return;
    }
    this.isDirty = true;
    if (marker === '*') {
      for (const node of this.selectedTreeNodes) {
        this.moveInTree({ originalEvent: event, dragNode: node, dropNode: this.treeNodes[0] }, 'bottom');
      }
      this.searchFromFieldValue(this.treeSearchValue);
      this.selectedTreeNodes = this.reselect(this.treeNodes[0]);
    } else {
      for (let i = this.selectedTreeNodes.length - 1; i >= 0; i--) {
        const node = this.selectedTreeNodes[i];
        const { parent } = node;
        const oldIndex = parent.children.indexOf(node);
        if (oldIndex < parent.children.length - 1 && !this.selectedTreeNodes.includes(parent.children[oldIndex + 1])) {
          node.parent = undefined;
          // déplacement dans l'arbre visuel
          parent.children = moveToNewIndex(parent.children, oldIndex, oldIndex + 1);
          // déplacement dans le vrai arbre
          this.moveInTree({ originalEvent: event, dragNode: node, dropNode: parent }, 'down');
          node.parent = parent;
        }
      }
    }
  }

  stepIn(event: Event): void {
    event?.stopPropagation();
    let changed = false;
    for (let i = this.selectedTreeNodes.length - 1; i >= 0; i--) {
      const node = this.selectedTreeNodes[i];
      const { parent } = node;
      const index = parent.children.indexOf(node);
      const next = parent.children[index + 1];
      if (next && next.data.isSummary) {
        if (!next.expanded) {
          this.expandNode({ originalEvent: event, node: next });
        }
        this.moveInTree({ originalEvent: event, dragNode: node, dropNode: next }, 'top');
        changed = true;
      }

      let isFollowed = true;
      while (i > 0 && isFollowed) {
        const _next = this.selectedTreeNodes[i - 1];
        if (_next.parent === parent && parent.children.indexOf(_next) === index - 1) {
          this.moveInTree({ originalEvent: event, dragNode: _next, dropNode: next }, 'top');
          i--;
        } else {
          isFollowed = false;
        }
      }
    }
    if (changed) {
      this.isDirty = true;
      this.searchFromFieldValue(this.treeSearchValue);
      this.selectedTreeNodes = this.reselect(this.treeNodes[0]);
    }
  }

  stepOut(event: Event): void {
    event?.stopPropagation();
    let changed = false;
    for (let i = this.selectedTreeNodes.length - 1; i >= 0; i--) {
      const node = this.selectedTreeNodes[i];
      const { parent } = node;
      if (parent.parent) {
        this.moveInTree(
          { originalEvent: event, dragNode: node, dropNode: parent.parent },
          parent.parent.children.indexOf(parent)
        );
        changed = true;
      }
    }
    if (changed) {
      this.isDirty = true;
      this.searchFromFieldValue(this.treeSearchValue);
      this.selectedTreeNodes = this.reselect(this.treeNodes[0]);
    }
  }

  private reselect(_in: TreeNode): TreeNode[] {
    const _selectedTreeNodes = [];
    if (this.selectedTreeNodes?.length) {
      for (const node of _in.children) {
        const _node = this.selectedTreeNodes.find((n) => n.data.id === node.data.id);
        if (_node) {
          _selectedTreeNodes.push(node);
        } else if (node.children?.length) {
          _selectedTreeNodes.push(...this.reselect(node));
        }
      }
    }
    return _selectedTreeNodes;
  }

  expandNode({ originalEvent, node }: { originalEvent: Event; node: TreeNode }): void {
    originalEvent?.stopPropagation();
    const path = pushInStack(node);
    const dataNode = getInTree(this.treeData.treeNodes, path);
    dataNode.expanded = true;
  }

  collapseNode({ originalEvent, node }: { originalEvent: Event; node: TreeNode }): void {
    originalEvent?.stopPropagation();
    const stack = pushInStack(node);
    const dataNode = getInTree(this.treeData.treeNodes, stack);
    dataNode.expanded = false;
  }
  //#endregion

  setConfig(config: string, value: any): void {
    if (typeof this[config] === 'boolean') {
      this[config] = value === 'Y';
    } else {
      this[config] = value;
    }
  }
}

type TreeLabelPart = { text: string; match: boolean };
